
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface SiteBooking {
  id: string;
  start_date: string;
  end_date: string;
  customer_name: string;
  payment_status: string;
  site_id: string;
}

export const useSiteBookings = (siteId: string | null) => {
  const [bookings, setBookings] = useState<SiteBooking[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!siteId) {
      console.log(`[useSiteBookings] No siteId provided, clearing bookings`);
      setBookings([]);
      return;
    }

    const fetchBookings = async () => {
      setIsLoading(true);
      try {
        console.log(`[useSiteBookings] Fetching bookings for site: ${siteId}`);
        
        const { data, error } = await supabase
          .from('site_bookings')
          .select('id, start_date, end_date, customer_name, payment_status, site_id')
          .eq('site_id', siteId)
          .eq('payment_status', 'paid')
          .order('start_date', { ascending: true });

        if (error) {
          console.error(`[useSiteBookings] Error fetching site bookings for ${siteId}:`, error);
          setBookings([]);
        } else {
          console.log(`[useSiteBookings] Raw database response for site ${siteId}:`, data);
          console.log(`[useSiteBookings] Found ${data?.length || 0} paid bookings for site ${siteId}`);
          
          // Log each booking in detail
          if (data && data.length > 0) {
            data.forEach((booking, index) => {
              console.log(`[useSiteBookings] Booking ${index + 1} for site ${siteId}:`, {
                id: booking.id,
                site_id: booking.site_id,
                customer_name: booking.customer_name,
                start_date: booking.start_date,
                end_date: booking.end_date,
                payment_status: booking.payment_status
              });
            });
          } else {
            console.log(`[useSiteBookings] No paid bookings found for site ${siteId}`);
          }
          
          setBookings(data || []);
        }
      } catch (error) {
        console.error(`[useSiteBookings] Exception fetching site bookings for ${siteId}:`, error);
        setBookings([]);
      } finally {
        setIsLoading(false);
      }
    };

    // Initial fetch
    fetchBookings();

    // Set up real-time subscription for booking changes
    const channel = supabase
      .channel(`site-bookings-${siteId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'site_bookings',
          filter: `site_id=eq.${siteId}`
        },
        (payload) => {
          console.log(`[useSiteBookings] Real-time update for site ${siteId}:`, payload);
          fetchBookings(); // Refetch to ensure we have the latest data
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [siteId]);

  // Force refresh function for troubleshooting
  const refreshBookings = async () => {
    if (!siteId) return;
    
    console.log(`[useSiteBookings] Force refreshing bookings for site ${siteId}`);
    setIsLoading(true);
    
    try {
      const { data, error } = await supabase
        .from('site_bookings')
        .select('id, start_date, end_date, customer_name, payment_status, site_id')
        .eq('site_id', siteId)
        .eq('payment_status', 'paid')
        .order('start_date', { ascending: true });

      if (error) {
        console.error(`[useSiteBookings] Error in force refresh for ${siteId}:`, error);
      } else {
        console.log(`[useSiteBookings] Force refresh successful for site ${siteId}, found ${data?.length || 0} bookings`);
        setBookings(data || []);
      }
    } catch (error) {
      console.error(`[useSiteBookings] Exception in force refresh for ${siteId}:`, error);
    } finally {
      setIsLoading(false);
    }
  };

  return { bookings, isLoading, refreshBookings };
};
